/**
 * Admin V3 — Compliance — SearchBar (Plan §2.2 M-F-15).
 * Pure presentation. No API calls.
 *
 * G11.4 — Filter empty-state UX:
 *   - state filter + search input both carry visible labels above them
 *     so the admin knows the filter exists and what it does, instead of
 *     "looking empty until interaction."
 *   - the state filter default option shows the count of available states.
 *
 * G11.5 — Owner forensic verdict UI remediation:
 *   - "search input text invisible" + "Activity Journal filter text invisible":
 *     Tailwind preflight sets `color: inherit` for form elements; the global
 *     `body { color: hsl(var(--foreground)) }` in index.css resolves to a
 *     light color (designed for dark hero sections). Admin V3 panels are
 *     white; result: light text on white = invisible. Fix: every input and
 *     select in this surface now carries explicit `text-slate-900` +
 *     `placeholder:text-slate-400` so typed values are readable.
 *   - "empty-state filter labels not properly rendered": labels upgraded
 *     from text-xs/slate-700 to text-sm/font-medium/slate-800 so they read
 *     as labels, not decoration.
 */
import React from 'react';
import { Search } from 'lucide-react';
import { lifecycleStateMeta } from '../../shared/visualTokens';
import { t } from '../../i18n';

// G11.5 — Shared form-element class fragment. Centralised so any future
// compliance form element inherits the same defensive text-slate-900 +
// placeholder treatment that fixes the body-color-inheritance trap.
const FORM_TEXT_CLS = 'text-slate-900 placeholder:text-slate-400';

const CompliancePilotSearchBar = ({
  search, onSearchChange,
  stateFilter, onStateFilterChange,
  locale = 'fr',
}) => {
  const states = Object.keys(lifecycleStateMeta);
  return (
    <div
      className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end"
      data-testid="compliance-pilot-searchbar"
    >
      <div className="relative flex-1 flex flex-col">
        <label
          htmlFor="compliance-pilot-search-input"
          className="text-sm font-medium text-slate-800 mb-1"
        >
          {t('compliance.dashboard.search.label', locale)}
        </label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          <input
            id="compliance-pilot-search-input"
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('compliance.dashboard.search.placeholder', locale)}
            className={`w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-300 ${FORM_TEXT_CLS}`}
            data-testid="compliance-pilot-search-input"
          />
        </div>
      </div>
      <div className="flex flex-col">
        <label
          htmlFor="compliance-pilot-state-filter"
          className="text-sm font-medium text-slate-800 mb-1"
        >
          {t('compliance.dashboard.filter.label', locale)}
        </label>
        <select
          id="compliance-pilot-state-filter"
          value={stateFilter}
          onChange={(e) => onStateFilterChange(e.target.value)}
          className={`px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-300 min-w-[220px] ${FORM_TEXT_CLS}`}
          data-testid="compliance-pilot-state-filter"
        >
          <option value="all">
            {t('compliance.dashboard.filter.all', locale)} ({states.length})
          </option>
          {states.map((s) => (
            <option key={s} value={s}>{t(lifecycleStateMeta[s].labelKey, locale)}</option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default CompliancePilotSearchBar;
